import { useSocketStore } from 'src/store/socket'
import { promisifyPush } from 'src/utils/promisifyPush'
import { BetMap } from 'src/types/mappers/BetMap'
import type { Bet } from 'src/types/domain/Bet'
import type { BetDTO } from 'src/actions/types'

/**
 * One-shot authoritative fetch of the player's server-side bets, WITHOUT
 * mutating the bet store.
 *
 * Deliberately NOT `usePlayerBets.fetchBets`: that calls `betActions.setBets`,
 * which REPLACES `bets.allIds` with only the fetched ids — mid-round that
 * truncates any still-pending local bets out of the settlement net. This
 * returns the server snapshot for the CALLER to reconcile against, touching
 * nothing.
 *
 * Reads the live channel from the socket store so it can run outside React
 * (the settlement handler is a plain util). Returns `undefined` when the
 * channel isn't joined or the push fails, so callers fall back to their
 * existing behaviour rather than blocking settlement.
 */
// Bound the reconcile fetch. `promisifyPush` only wires 'ok'/'error', not
// Phoenix's 'timeout' reply, so a dropped bets:get response would leave the
// promise permanently unsettled — and settlement awaits this, so it would hang
// the whole settle pass. Race it against a timeout that resolves to `undefined`
// so the caller always falls back to its safe purge path.
const SNAPSHOT_TIMEOUT_MS = 3000
const TIMED_OUT = Symbol('bets-get-timeout')

export const fetchServerBetsSnapshot = async (): Promise<Bet[] | undefined> => {
  const channel = useSocketStore.getState().playerChannel
  if (!channel || channel.state !== 'joined') return undefined
  try {
    const push = promisifyPush({ channel, eventName: 'client:player:bets:get' })
    const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
      setTimeout(() => resolve(TIMED_OUT), SNAPSHOT_TIMEOUT_MS)
    })
    const result = await Promise.race([push, timeout])
    if (result === TIMED_OUT) return undefined
    return (result as BetDTO[]).map(BetMap.fromBetDTO)
  } catch {
    return undefined
  }
}
